import React, { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { Alert, Button } from 'reactstrap';
import { TypedMemo } from '../common/types';
import { useTRPCClientAPI, useTRPCMutation } from '../../trpc/trpc';
import { useShowToast } from '../../redux/slices/toasts/hooks';
import { workspaceMessages } from '../workspaces/workspaceMessages';
import { useWorkspaces } from '../workspaces/useWorkspaces';
import { LeaveWorkspaceModalBody } from '../workspaces/LeaveWorkspaceModalBody';
import { useApplyFreshUserToken } from './useApplyFreshUserToken';
import { leaveErrorMessage } from './leaveErrorMessage';
import { useSMLRouter } from '../../routing/sml/hooks';
import { SMLRoutes } from '../../routing/sml/SMLRoutes';
import { TeamConfirmModal } from './TeamConfirmModal';
import { Panel, PanelBody, PanelHead } from '../music24/components/primitives/Panel';

interface TeamLeaveCardProps {
  /** The workspace the member leaves, as `team.list` names it. */
  workspaceName: string;
  /** The workspace the member leaves: the active one. */
  licenseId: number;
}

/**
 * The member's "Leave {workspace}" danger box (design: workspaces e1). The button opens the e2
 * confirm. On an error the modal closes and the error shows under the button row (e3). After the
 * leave, the home token applies, Home opens and a toast says "You left {workspace}." (e2).
 */
const TeamLeaveCardComponent: React.FC<TeamLeaveCardProps> = ({ workspaceName, licenseId }) => {
  const intl = useIntl();
  const trpcClient = useTRPCClientAPI();
  const applyToken = useApplyFreshUserToken();
  const router = useSMLRouter();
  const { showSuccess } = useShowToast();
  const { workspaces } = useWorkspaces();
  // design: workspaces e1 / e2 name the home workspace and show the row of the one to leave.
  const { homeName, leaving } = useMemo(
    () => ({
      homeName: workspaces.find((workspace) => workspace.isHome)?.name ?? null,
      leaving: workspaces.find((workspace) => workspace.licenseId === licenseId) ?? null,
    }),
    [licenseId, workspaces]
  );
  const [isConfirming, setConfirming] = useState(false);
  // The session token `team.leave` returned. From then on the person is out of the team, so a
  // failed switch retries only `applyToken`, never `team.leave`.
  const [freshToken, setFreshToken] = useState<string | null>(null);
  const finishLeave = useTRPCMutation({
    mutationFn: (token: string) => applyToken(token),
    onSuccess: () => {
      router.replace(SMLRoutes.home);
      showSuccess(intl.formatMessage(workspaceMessages.left, { workspace: workspaceName }));
    },
    onError: () => setConfirming(false),
  });
  const leave = useTRPCMutation({
    mutationFn: () => trpcClient.team.leave.mutate({ licenseId }),
    onSuccess: (result: { token: string }) => {
      setFreshToken(result.token);
      finishLeave.mutate(result.token);
    },
    onError: () => setConfirming(false),
  });
  const open = useCallback(() => setConfirming(true), []);
  const close = useCallback(() => setConfirming(false), []);
  const confirm = useCallback(() => leave.mutate(), [leave]);
  const retryFinish = useCallback(() => {
    if (freshToken !== null) {
      finishLeave.mutate(freshToken);
    }
  }, [finishLeave, freshToken]);
  const isPending = leave.isPending || finishLeave.isPending;
  const hasLeft = freshToken !== null;

  const buttonLabel = intl.formatMessage({ id: 'app.sml.team.leave.button', defaultMessage: 'Leave workspace' });

  return (
    <Panel as="section" tone="danger" className="mt-4">
      <PanelHead
        title={intl.formatMessage(
          { id: 'app.sml.team.leave.heading', defaultMessage: 'Leave {workspace}' },
          { workspace: workspaceName }
        )}
        subtitle={
          homeName === null
            ? intl.formatMessage({
                id: 'app.sml.team.leave.description',
                defaultMessage:
                  'You lose access to its artists, pages and campaigns. Your own workspace stays as it is.',
              })
            : intl.formatMessage(
                {
                  id: 'app.sml.team.leave.descriptionNamed',
                  defaultMessage:
                    'You lose access to its artists, pages and campaigns. Your own workspace, {home}, stays as it is.',
                },
                { home: homeName }
              )
        }
        right={
          <Button color="danger" outline disabled={isPending || hasLeft} onClick={open}>
            {buttonLabel}
          </Button>
        }
      />
      {(Boolean(leave.error) || (hasLeft && Boolean(finishLeave.error))) && (
        <PanelBody stack>
          {leave.error && (
            <Alert color="danger" className="mb-0">
              {leaveErrorMessage(intl, leave.error, workspaceName)}
            </Alert>
          )}
          {hasLeft && finishLeave.error && (
            <Alert color="danger" className="mb-0 d-flex flex-wrap align-items-center justify-content-between gap-2">
              {leaveErrorMessage(intl, finishLeave.error, workspaceName)}
              <Button color="danger" size="sm" disabled={finishLeave.isPending} onClick={retryFinish}>
                {intl.formatMessage({ id: 'app.sml.team.accept.tryAgain', defaultMessage: 'Try again' })}
              </Button>
            </Alert>
          )}
        </PanelBody>
      )}
      <TeamConfirmModal
        isOpen={isConfirming}
        title={intl.formatMessage(
          { id: 'app.sml.team.leave.title', defaultMessage: 'Leave {workspace}?' },
          { workspace: workspaceName }
        )}
        confirmLabel={intl.formatMessage({ id: 'app.sml.team.leave.confirm', defaultMessage: 'Leave workspace' })}
        isPending={isPending}
        onConfirm={confirm}
        onCancel={close}
      >
        <LeaveWorkspaceModalBody
          workspace={leaving}
          homeName={homeName}
          fallbackBody={intl.formatMessage({
            id: 'app.sml.team.leave.body',
            defaultMessage:
              'You lose access to its artists, pages and campaigns right away. Your own workspace stays as it is. To come back, ask the owner for a new invite.',
          })}
        />
      </TeamConfirmModal>
    </Panel>
  );
};

export const TeamLeaveCard = TypedMemo(TeamLeaveCardComponent);
